import { useState } from "react";
import { Layers, LayoutDashboard } from "lucide-react";
import { ALL_ACCOUNTS_ID, type AccountDto } from "../../../shared/types";
import type { ActiveTab } from "../../App";
import { useDeleteAccount } from "../../hooks/useApi";
import { providerMeta } from "../providerMeta";
import { AddAccountButton } from "./AddAccountButton";
import { ConfirmDialog } from "../common/ConfirmDialog";

interface TabBarProps {
  accounts: AccountDto[];
  activeTab: ActiveTab;
  onSelect: (tab: ActiveTab) => void;
}

function Tab({
  active,
  onClick,
  children,
  title,
  color,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  title?: string;
  /** Account pastel used as the tab background. */
  color?: string;
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      // Inactive pastel tabs get ~45% alpha (hex suffix) so the active one pops.
      style={color ? { backgroundColor: active ? color : `${color}73` } : undefined}
      className={`group relative flex h-full max-w-[220px] items-center gap-1.5 rounded-t px-3.5 text-[13px] whitespace-nowrap ${
        active
          ? `font-semibold ${color ? "text-ink" : "text-accent"}`
          : "text-ink-muted hover:text-ink"
      }`}
    >
      {children}
      {active && <span className="absolute inset-x-2 bottom-0 h-[3px] rounded-t bg-accent" />}
    </button>
  );
}

export function TabBar({ accounts, activeTab, onSelect }: TabBarProps) {
  const deleteAccount = useDeleteAccount();
  const [pendingRemove, setPendingRemove] = useState<AccountDto | null>(null);

  const confirmRemove = () => {
    const account = pendingRemove;
    if (!account) return;
    deleteAccount.mutate(account.id, {
      onSuccess: () => {
        setPendingRemove(null);
        if (activeTab === account.id) onSelect(null);
      },
      onError: () => setPendingRemove(null),
    });
  };

  return (
    <div className="flex h-11 shrink-0 items-stretch overflow-x-auto border-b border-stroke bg-white px-2">
      <Tab active={activeTab === null} onClick={() => onSelect(null)}>
        <LayoutDashboard size={14} strokeWidth={2} className="shrink-0" />
        Dashboard
      </Tab>

      <Tab
        active={activeTab === ALL_ACCOUNTS_ID}
        onClick={() => onSelect(ALL_ACCOUNTS_ID)}
        title="All accounts combined"
      >
        <Layers size={14} strokeWidth={2} className="shrink-0" />
        All accounts
      </Tab>

      {accounts.map((account, index) => (
        <Tab
          key={account.id}
          active={activeTab === account.id}
          onClick={() => onSelect(account.id)}
          title={`${account.displayName ?? account.username} (${providerMeta(account.provider).label})`}
          color={account.color}
        >
          <span
            className="grid size-5 shrink-0 place-items-center rounded-full text-[10px] font-bold text-white"
            style={{ backgroundColor: providerMeta(account.provider).color }}
          >
            {index + 1}
          </span>
          <span className="truncate">{account.username}</span>
          <span
            role="button"
            aria-label={`Remove ${account.username}`}
            onClick={(e) => {
              e.stopPropagation();
              setPendingRemove(account);
            }}
            className="ml-0.5 hidden rounded px-1 text-[12px] text-ink-muted hover:bg-black/10 hover:text-ink group-hover:block"
          >
            ×
          </span>
        </Tab>
      ))}

      <AddAccountButton variant="tab" />
      {pendingRemove && (
        <ConfirmDialog
          title={`Remove ${pendingRemove.username}?`}
          message={
            <span className="block space-y-2">
              <span className="block">
                This disconnects the account from mysmall.life and deletes everything the app
                stores for it:
              </span>
              <span className="block pl-4">
                • all cached emails and attachments
                <br />• its mail in your folders and AI groups
                <br />• sign-in tokens and sync state
              </span>
              <span className="block">
                Your real mailbox at {providerMeta(pendingRemove.provider).label} is NOT touched —
                no emails are deleted from the provider. You can reconnect the account later, but
                it will re-sync from scratch.
              </span>
            </span>
          }
          confirmLabel="Remove account"
          busy={deleteAccount.isPending}
          onConfirm={confirmRemove}
          onCancel={() => setPendingRemove(null)}
        />
      )}
    </div>
  );
}
